import React, { useMemo, useState } from 'react';
import { Lead, Team } from '../types';
import { LeadTable } from './LeadTable';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Search, Filter, ArrowRight, Users, Grab, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface LeadPoolProps {
  leads: Lead[];
  teams: Team[];
  onAssignToTeam: (leadIds: string[], teamId: string) => void;
  onNext: () => void;
}
export function LeadPool({
  leads,
  teams,
  onAssignToTeam,
  onNext
}: LeadPoolProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  // Filter leads that are unassigned
  const unassignedLeads = useMemo(() => {
    return leads.filter(l => l.status === 'unassigned');
  }, [leads]);
  // Apply search and source filters
  const filteredLeads = useMemo(() => {
    return unassignedLeads.filter(lead => {
      const matchesSearch = lead.name.toLowerCase().includes(searchQuery.toLowerCase()) || lead.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSource = sourceFilter === 'all' || lead.source === sourceFilter;
      return matchesSearch && matchesSource;
    });
  }, [unassignedLeads, searchQuery, sourceFilter]);
  // Get unique sources for filter dropdown
  const sources = useMemo(() => {
    const unique = new Set(leads.map(l => l.source));
    return Array.from(unique);
  }, [leads]);
  const handleToggleSelect = (leadId: string) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLeads(new Set(filteredLeads.map(l => l.id)));
    } else {
      setSelectedLeads(new Set());
    }
  };
  const handleGrabNext = (count: number) => {
    // Grab the next N leads from the filtered list that aren't already selected
    const currentSelected = new Set(selectedLeads);
    let grabbed = 0;
    for (const lead of filteredLeads) {
      if (grabbed >= count) break;
      if (!currentSelected.has(lead.id)) {
        currentSelected.add(lead.id);
        grabbed++;
      }
    }
    setSelectedLeads(currentSelected);
  };
  const handleClearSelection = () => {
    setSelectedLeads(new Set());
  };
  const handleAssign = () => {
    if (!selectedTeamId || selectedLeads.size === 0) return;
    onAssignToTeam(Array.from(selectedLeads), selectedTeamId);
    setSelectedLeads(new Set());
    setSelectedTeamId('');
  };
  const allAssigned = unassignedLeads.length === 0 && leads.length > 0;
  const GRAB_AMOUNTS = [10, 20, 30, 50];
  return <div className="space-y-6" data-id="element-49">
      {/* Header Stats */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-slate-200 shadow-sm" data-id="element-50">
        <div data-id="element-51">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-52">Lead Pool</h2>
          <p className="text-sm text-slate-500" data-id="element-53">
            Select leads and assign them to a team to proceed.
          </p>
        </div>
        <div className="flex items-center space-x-4" data-id="element-54">
          <div className="text-right" data-id="element-55">
            <div className="text-2xl font-bold text-slate-900" data-id="element-56">
              {unassignedLeads.length}
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wide" data-id="element-57">
              Unassigned
            </div>
          </div>
          <div className="h-8 w-px bg-slate-200" data-id="element-58" />
          <div className="text-right" data-id="element-59">
            <div className="text-2xl font-bold text-slate-400" data-id="element-60">
              {leads.length}
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wide" data-id="element-61">
              Total Leads
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4" data-id="element-62">
        <div className="flex-1" data-id="element-63">
          <Input placeholder="Search by name or email..." icon={<Search className="h-4 w-4" data-id="element-65" />} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-64" />
        </div>
        <div className="w-full sm:w-64" data-id="element-66">
          <div className="relative" data-id="element-67">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400" data-id="element-68">
              <Filter className="h-4 w-4" data-id="element-69" />
            </div>
            <select className="block w-full pl-10 pr-10 py-2 text-base border-slate-300 focus:outline-none focus:ring-[#0D9488] focus:border-[#0D9488] sm:text-sm rounded-md" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} data-id="element-70">
              <option value="all" data-id="element-71">All Sources</option>
              {sources.map(source => <option key={source} value={source} data-id="element-72">
                  {source}
                </option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Quick Select Bar */}
      <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg border border-slate-200 shadow-sm" data-id="element-73">
        <div className="flex items-center gap-3" data-id="element-74">
          <span className="text-sm font-medium text-slate-600 whitespace-nowrap" data-id="element-75">
            Quick Select:
          </span>
          <div className="flex items-center gap-1.5" data-id="element-76">
            {GRAB_AMOUNTS.map(amount => <button key={amount} onClick={() => handleGrabNext(amount)} disabled={filteredLeads.length === 0 || filteredLeads.every(l => selectedLeads.has(l.id))} className={`
                  px-3 py-1.5 text-xs font-semibold rounded-md transition-all
                  border border-slate-200
                  hover:border-[#0D9488] hover:text-[#0D9488] hover:bg-teal-50
                  disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-400 disabled:hover:bg-white
                  ${selectedLeads.size > 0 ? 'text-slate-600 bg-white' : 'text-slate-600 bg-white'}
                `} data-id="element-77">
                Next {amount}
              </button>)}
            <button onClick={() => handleGrabNext(filteredLeads.length)} disabled={filteredLeads.length === 0 || filteredLeads.every(l => selectedLeads.has(l.id))} className={`
                px-3 py-1.5 text-xs font-semibold rounded-md transition-all
                border border-slate-200
                hover:border-[#0D9488] hover:text-[#0D9488] hover:bg-teal-50
                disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-400 disabled:hover:bg-white
                text-slate-600 bg-white
              `} data-id="element-78">
              All (
              {filteredLeads.filter(l => !selectedLeads.has(l.id)).length})
            </button>
          </div>
        </div>
        {selectedLeads.size > 0 && <button onClick={handleClearSelection} className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors" data-id="element-79">
            <X className="h-3.5 w-3.5" data-id="element-80" />
            Clear {selectedLeads.size} selected
          </button>}
      </div>

      {/* Table */}
      <LeadTable leads={filteredLeads} selectedLeads={selectedLeads} onToggleSelect={handleToggleSelect} onSelectAll={handleSelectAll} showSelection={true} data-id="element-81" />

      {/* Floating Action Bar */}
      <AnimatePresence data-id="element-82">
        {selectedLeads.size > 0 && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white px-6 py-4 rounded-lg shadow-xl z-50 flex items-center gap-4 w-[90%] max-w-2xl" data-id="element-83">
            <div className="font-medium whitespace-nowrap" data-id="element-84">
              {selectedLeads.size} leads selected
            </div>
            <div className="h-6 w-px bg-slate-700" data-id="element-85" />
            <div className="flex-1 flex items-center gap-3" data-id="element-86">
              <span className="text-sm text-slate-300 whitespace-nowrap" data-id="element-87">
                Assign to:
              </span>
              <select className="bg-slate-800 border-slate-700 text-white text-sm rounded-md focus:ring-[#0D9488] focus:border-[#0D9488] block w-full" value={selectedTeamId} onChange={e => setSelectedTeamId(e.target.value)} data-id="element-88">
                <option value="" data-id="element-89">Select a Team...</option>
                {teams.map(team => <option key={team.id} value={team.id} data-id="element-90">
                    {team.name}
                  </option>)}
              </select>
            </div>
            <Button variant="primary" onClick={handleAssign} disabled={!selectedTeamId} size="sm" data-id="element-91">
              Assign
            </Button>
          </motion.div>}
      </AnimatePresence>

      {/* Next Step Action */}
      <div className="flex justify-end pt-4" data-id="element-92">
        <Button variant="primary" size="lg" rightIcon={<ArrowRight className="h-4 w-4" data-id="element-94" />} onClick={onNext} disabled={!allAssigned} data-id="element-93">
          Next: Distribute
        </Button>
      </div>
    </div>;
}